import { defineMessages, type IntlShape } from 'react-intl';

import {
	type MarketoFieldLocalization,
	type MarketoFormLocalization,
	normalizeMarketoKey,
} from './marketo-form-localization';

const messages = defineMessages({
	firstNameLabel: {
		id: 'uniform.marketoForm.field.firstName.label',
		defaultMessage: 'First Name',
	},
	lastNameLabel: {
		id: 'uniform.marketoForm.field.lastName.label',
		defaultMessage: 'Last Name',
	},
	businessEmailLabel: {
		id: 'uniform.marketoForm.field.businessEmail.label',
		defaultMessage: 'Email',
	},
	businessEmailInstruction: {
		id: 'uniform.marketoForm.field.businessEmail.instruction',
		defaultMessage: 'Please enter your work email.',
	},
	companyLabel: {
		id: 'uniform.marketoForm.field.company.label',
		defaultMessage: 'Company',
	},
	jobTitleLabel: {
		id: 'uniform.marketoForm.field.jobTitle.label',
		defaultMessage: 'Job Title',
	},
	phoneLabel: {
		id: 'uniform.marketoForm.field.phone.label',
		defaultMessage: 'Work Phone #',
	},
	stateLabel: {
		id: 'uniform.marketoForm.field.state.label',
		defaultMessage: 'State',
	},
	requestTypeLabel: {
		id: 'uniform.marketoForm.field.requestType.label',
		defaultMessage: 'Request Type',
	},
	commentsLabel: {
		id: 'uniform.marketoForm.field.comments.label',
		defaultMessage: 'Comments',
	},
	departmentLabel: {
		id: 'uniform.marketoForm.field.department.label',
		defaultMessage: 'Department',
	},
	roleLabel: {
		id: 'uniform.marketoForm.field.role.label',
		defaultMessage: 'Role (C)',
	},
	personaRoleLabel: {
		id: 'uniform.marketoForm.field.personaRole.label',
		defaultMessage: 'Which best describes your role?',
	},
	opportunityDescriptionLabel: {
		id: 'uniform.marketoForm.field.opportunityDescription.label',
		defaultMessage: 'Your message',
	},
	companySizeLabel: {
		id: 'uniform.marketoForm.field.companySize.label',
		defaultMessage: 'Company Size',
	},
	departmentSizeLabel: {
		id: 'uniform.marketoForm.field.departmentSize.label',
		defaultMessage: 'Department Size',
	},
	productInterestLabel: {
		id: 'uniform.marketoForm.field.productInterest.label',
		defaultMessage: 'What are you primarily interested in?',
	},
	pickYourProductsLabel: {
		id: 'uniform.marketoForm.field.pickYourProducts.label',
		defaultMessage: 'Pick your Products',
	},
	physicalCopyRequestedLabel: {
		id: 'uniform.marketoForm.field.physicalCopyRequested.label',
		defaultMessage: 'Send a physical copy of the handbook (US Only)',
	},
	jsmUserCountLabel: {
		id: 'uniform.marketoForm.field.jsmUserCount.label',
		defaultMessage: 'Number of Agents',
	},
	countryLabel: {
		id: 'uniform.marketoForm.field.country.label',
		defaultMessage: 'Country',
	},
	selectCountryOption: {
		id: 'uniform.marketoForm.option.selectCountry',
		defaultMessage: 'Select...',
	},
	marketingConsentLabel: {
		id: 'uniform.marketoForm.field.marketingConsent.label',
		defaultMessage:
			'Yes! I would like to receive promotional emails including product news, events and more from Atlassian and its affiliates.',
	},
	privacyPolicyLink: {
		id: 'uniform.marketoForm.privacyPolicy.link',
		defaultMessage: 'Privacy Policy',
	},
	privacyPolicyHtml: {
		id: 'uniform.marketoForm.field.privacyPolicy.html',
		defaultMessage:
			"By submitting this form, I acknowledge receipt of Atlassian's {privacyPolicyLink}.",
	},
	submitButton: {
		id: 'uniform.marketoForm.submitButton',
		defaultMessage: 'Submit',
	},
	yesOption: {
		id: 'uniform.marketoForm.option.yes',
		defaultMessage: 'Yes',
	},
	noOption: {
		id: 'uniform.marketoForm.option.no',
		defaultMessage: 'No',
	},
	requiredValidation: {
		id: 'uniform.marketoForm.validation.required',
		defaultMessage: 'This field is required.',
	},
	emailValidation: {
		id: 'uniform.marketoForm.validation.email',
		defaultMessage: 'Please enter a valid email address',
	},
	emailRealAddressValidation: {
		id: 'uniform.marketoForm.validation.emailRealAddress',
		defaultMessage: 'Please type a real email address.',
	},
	businessEmailDomainValidation: {
		id: 'uniform.marketoForm.validation.businessEmailDomain',
		defaultMessage:
			'Please enter your business email address. This form does not accept addresses from {domain}',
	},
});

const PRIVACY_POLICY_HREF = 'https://www.atlassian.com/legal/privacy-policy';

const addFieldAliases = (
	fields: Record<string, MarketoFieldLocalization>,
	aliases: string[],
	field: MarketoFieldLocalization,
) => {
	aliases.forEach((alias) => {
		fields[normalizeMarketoKey(alias)] = field;
	});
};

const getPrivacyPolicyHtml = (intl: IntlShape): string =>
	intl.formatMessage(messages.privacyPolicyHtml, {
		privacyPolicyLink: `<a href="${PRIVACY_POLICY_HREF}" rel="noopener noreferrer" target="_blank">${intl.formatMessage(
			messages.privacyPolicyLink,
		)}</a>`,
	});

export const getMarketoFormLocalization = (intl: IntlShape): MarketoFormLocalization => {
	const fields: Record<string, MarketoFieldLocalization> = {};
	const firstName = {
		label: intl.formatMessage(messages.firstNameLabel),
		ariaLabel: intl.formatMessage(messages.firstNameLabel),
	};
	const lastName = {
		label: intl.formatMessage(messages.lastNameLabel),
		ariaLabel: intl.formatMessage(messages.lastNameLabel),
	};
	const businessEmail = {
		label: intl.formatMessage(messages.businessEmailLabel),
		ariaLabel: intl.formatMessage(messages.businessEmailLabel),
		instructionHtml: intl.formatMessage(messages.businessEmailInstruction),
		placeholder: ' ',
	};
	const company = {
		label: intl.formatMessage(messages.companyLabel),
		ariaLabel: intl.formatMessage(messages.companyLabel),
	};
	const jobTitle = {
		label: intl.formatMessage(messages.jobTitleLabel),
		ariaLabel: intl.formatMessage(messages.jobTitleLabel),
	};
	const phone = {
		label: intl.formatMessage(messages.phoneLabel),
		ariaLabel: intl.formatMessage(messages.phoneLabel),
	};
	const state = {
		label: intl.formatMessage(messages.stateLabel),
		ariaLabel: intl.formatMessage(messages.stateLabel),
	};
	const requestType = {
		label: intl.formatMessage(messages.requestTypeLabel),
		ariaLabel: intl.formatMessage(messages.requestTypeLabel),
	};
	const comments = {
		label: intl.formatMessage(messages.commentsLabel),
		ariaLabel: intl.formatMessage(messages.commentsLabel),
	};
	const department = {
		label: intl.formatMessage(messages.departmentLabel),
		ariaLabel: intl.formatMessage(messages.departmentLabel),
	};
	const role = {
		label: intl.formatMessage(messages.roleLabel),
		ariaLabel: intl.formatMessage(messages.roleLabel),
	};
	const personaRole = {
		label: intl.formatMessage(messages.personaRoleLabel),
		ariaLabel: intl.formatMessage(messages.personaRoleLabel),
	};
	const opportunityDescription = {
		label: intl.formatMessage(messages.opportunityDescriptionLabel),
		ariaLabel: intl.formatMessage(messages.opportunityDescriptionLabel),
	};
	const companySize = {
		label: intl.formatMessage(messages.companySizeLabel),
		ariaLabel: intl.formatMessage(messages.companySizeLabel),
	};
	const departmentSize = {
		label: intl.formatMessage(messages.departmentSizeLabel),
		ariaLabel: intl.formatMessage(messages.departmentSizeLabel),
	};
	const productInterest = {
		label: intl.formatMessage(messages.productInterestLabel),
		ariaLabel: intl.formatMessage(messages.productInterestLabel),
	};
	const pickYourProducts = {
		label: intl.formatMessage(messages.pickYourProductsLabel),
		ariaLabel: intl.formatMessage(messages.pickYourProductsLabel),
	};
	const physicalCopyRequested = {
		label: intl.formatMessage(messages.physicalCopyRequestedLabel),
		ariaLabel: intl.formatMessage(messages.physicalCopyRequestedLabel),
	};
	const jsmUserCount = {
		label: intl.formatMessage(messages.jsmUserCountLabel),
		ariaLabel: intl.formatMessage(messages.jsmUserCountLabel),
	};
	const country = {
		label: intl.formatMessage(messages.countryLabel),
		ariaLabel: intl.formatMessage(messages.countryLabel),
	};
	const marketingConsent = {
		label: intl.formatMessage(messages.marketingConsentLabel),
		ariaLabel: intl.formatMessage(messages.marketingConsentLabel),
	};
	const privacyPolicy = {
		html: getPrivacyPolicyHtml(intl),
	};

	addFieldAliases(fields, ['FirstName', 'first_name', 'firstname'], firstName);
	addFieldAliases(fields, ['LastName', 'last_name', 'lastname'], lastName);
	addFieldAliases(
		fields,
		['Email', 'emailAddress', 'businessEmail', 'business_email'],
		businessEmail,
	);
	addFieldAliases(fields, ['Company', 'companyName', 'company_name'], company);
	addFieldAliases(fields, ['Title', 'JobTitle', 'job_title'], jobTitle);
	addFieldAliases(fields, ['Phone', 'PhoneNumber', 'phone_number'], phone);
	addFieldAliases(fields, ['State'], state);
	addFieldAliases(fields, ['RequestType', 'Request_Type'], requestType);
	addFieldAliases(fields, ['Comments'], comments);
	addFieldAliases(fields, ['Department'], department);
	addFieldAliases(fields, ['Role__c', 'RoleC'], role);
	addFieldAliases(fields, ['Persona_Role__c', 'PersonaRoleC'], personaRole);
	addFieldAliases(
		fields,
		['Opportunity_Description__c', 'OpportunityDescriptionC'],
		opportunityDescription,
	);
	addFieldAliases(
		fields,
		['Company_Size_Range_from_User__c', 'CompanySizeRangeFromUserC'],
		companySize,
	);
	addFieldAliases(fields, ['Department_Size__c', 'DepartmentSizeC'], departmentSize);
	addFieldAliases(fields, ['ProductInterest__c', 'ProductInterestC'], productInterest);
	addFieldAliases(fields, ['PickYourProducts', 'Pick_Your_Products'], pickYourProducts);
	addFieldAliases(
		fields,
		['formPhysicalCopyRequested', 'PhysicalCopyRequested'],
		physicalCopyRequested,
	);
	addFieldAliases(fields, ['JSMUserCountfromUser', 'JSMUserCountFromUser'], jsmUserCount);
	addFieldAliases(fields, ['Country'], country);
	addFieldAliases(
		fields,
		['form_gmoiGranted', 'gmoiGranted', 'marketingConsent', 'marketing_consent'],
		marketingConsent,
	);
	addFieldAliases(fields, ['form_privacyPolicy', 'privacyPolicy', 'privacy_policy'], privacyPolicy);

	const selectCountry = intl.formatMessage(messages.selectCountryOption);
	const yes = intl.formatMessage(messages.yesOption);
	const no = intl.formatMessage(messages.noOption);
	const required = intl.formatMessage(messages.requiredValidation);
	const email = intl.formatMessage(messages.emailValidation);
	const emailRealAddress = intl.formatMessage(messages.emailRealAddressValidation);
	const businessEmailDomain = intl.formatMessage(messages.businessEmailDomainValidation, {
		domain: '{{domain}}',
	});

	return {
		locale: intl.locale,
		submitButton: intl.formatMessage(messages.submitButton),
		fields,
		options: {
			'': selectCountry,
			'country:': selectCountry,
			'departmentsizec:': selectCountry,
			'jsmusercountfromuser:': selectCountry,
			true: yes,
			yes,
			false: no,
			no,
		},
		validationMessages: {
			'this field is required': required,
			'this field is required.': required,
			'must be valid email': email,
			'must be valid email.': email,
			'must be valid email. example@yourdomain.com': email,
			'please enter a valid email address': email,
			'please enter a valid email address.': email,
			'please type a real email address': emailRealAddress,
			'please type a real email address.': emailRealAddress,
			'please enter your business email address. this form does not accept addresses from':
				businessEmailDomain,
			'please enter your business email address. this form does not accept addresses from {{domain}}':
				businessEmailDomain,
		},
	};
};
